import { z } from "zod"

import {
  signatureConsentVersion,
  signatureDrawnPngMaxBytes,
  signatureEventTypes,
  signatureFieldKinds,
  signatureMaxBytes,
  signatureMaxFields,
  signatureMethods,
  signatureScanStatuses,
  signatureStatuses,
  signatureVoidReasons,
} from "../../portal-signatures.js"

const Sha256Schema = z.string().regex(/^[a-f0-9]{64}$/)

export const SignaturePageSchema = z.object({
  width: z.number().positive().max(14_400),
  height: z.number().positive().max(14_400),
  rotate: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]),
  left: z.number().finite(),
  bottom: z.number().finite(),
})

export const SignatureFieldSchema = z.object({
  kind: z.enum(signatureFieldKinds),
  page: z.number().int().min(1),
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().positive().finite(),
  height: z.number().positive().finite(),
})

/** The letter on a "Document to sign" item. `originalToken` comes from the
 * upload confirm; leaving it out keeps the PDF already on the item. The
 * signer is a listed portal contact; on an activation it defaults to the
 * invited recipient. */
export const ItemSignatureInputSchema = z.object({
  originalToken: z.string().min(1).max(8192).optional(),
  signerContactId: z.uuid().nullable().optional(),
  fields: z.array(SignatureFieldSchema).max(signatureMaxFields).default([]),
})
export type ItemSignatureInput = z.infer<typeof ItemSignatureInputSchema>

/** A new version for an item that is already out. `expectedVersion` stops a
 * second firm tab from replacing a version it never saw. */
export const ApiSaveItemSignatureBodySchema = ItemSignatureInputSchema.extend({
  expectedVersion: z.number().int().min(1).optional(),
})
export type ApiSaveItemSignatureBody = z.input<
  typeof ApiSaveItemSignatureBodySchema
>

export const ApiSignatureOriginalResponseSchema = z.object({
  originalToken: z.string(),
  fileName: z.string(),
  byteSize: z.number().int().positive(),
  sha256: Sha256Schema,
  pageCount: z.number().int().min(1),
  pages: z.array(SignaturePageSchema),
})
export type ApiSignatureOriginalResponse = z.infer<
  typeof ApiSignatureOriginalResponseSchema
>

/** What an item row needs to show its signing state, on both sides. */
export const PortalItemSignatureSchema = z.object({
  signatureId: z.uuid(),
  version: z.number().int().min(1),
  status: z.enum(signatureStatuses),
  fileName: z.string(),
  pageCount: z.number().int().min(1),
  signerContactId: z.uuid().nullable(),
  signerName: z.string().nullable(),
  sentAt: z.iso.datetime().nullable(),
  signedAt: z.iso.datetime().nullable(),
  signedFileId: z.uuid().nullable(),
  voidReason: z.enum(signatureVoidReasons).nullable(),
})
export type PortalItemSignature = z.infer<typeof PortalItemSignatureSchema>

export const ApiSignatureEventSchema = z.object({
  seq: z.number().int().min(1),
  eventType: z.enum(signatureEventTypes),
  occurredAt: z.iso.datetime(),
  actorName: z.string().nullable(),
  contactId: z.uuid().nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  hash: Sha256Schema,
})

export const ApiSignatureRecordResponseSchema = z.object({
  signature: PortalItemSignatureSchema.extend({
    requestItemId: z.uuid().nullable(),
    originalSha256: Sha256Schema,
    signedSha256: Sha256Schema.nullable(),
    signerEmail: z.string().nullable(),
    signatureMethod: z.enum(signatureMethods).nullable(),
    scanStatus: z.enum(signatureScanStatuses),
    pages: z.array(SignaturePageSchema),
    fields: z.array(SignatureFieldSchema),
    voidedAt: z.iso.datetime().nullable(),
  }),
  versions: z.array(PortalItemSignatureSchema),
  events: z.array(ApiSignatureEventSchema),
  /** The event chain re-walked from the original's hash. False means a stored
   * event no longer matches its hash. */
  recordIntact: z.boolean(),
})
export type ApiSignatureRecordResponse = z.infer<
  typeof ApiSignatureRecordResponseSchema
>

export const PortalSignatureConsentSchema = z.object({
  version: z.literal(signatureConsentVersion),
  title: z.string(),
  paragraphs: z.array(z.string()),
  checkbox: z.string(),
  intent: z.string(),
})

export const PortalSignatureResponseSchema = z.object({
  signatureId: z.uuid(),
  version: z.number().int().min(1),
  status: z.enum(signatureStatuses),
  voidReason: z.enum(signatureVoidReasons).nullable(),
  /** The item's name, as the client's task list shows it. The screen, the
   * consent and the signed copy use it; `fileName` names the download. */
  documentName: z.string(),
  fileName: z.string(),
  firmName: z.string(),
  pageCount: z.number().int().min(1),
  pages: z.array(SignaturePageSchema),
  fields: z.array(SignatureFieldSchema),
  originalSha256: Sha256Schema,
  signerIsYou: z.boolean(),
  signerName: z.string().nullable(),
  /** The date field's value if you sign today, in the firm's time zone. */
  dateToday: z.string(),
  consent: PortalSignatureConsentSchema,
  consented: z.boolean(),
  signedAt: z.iso.datetime().nullable(),
  signedFileId: z.uuid().nullable(),
})
export type PortalSignatureResponse = z.infer<
  typeof PortalSignatureResponseSchema
>

export const PortalSignatureConsentBodySchema = z.object({
  consentVersion: z.literal(signatureConsentVersion),
})

/** A base64 PNG, without the data: prefix, at most 200 KB decoded. */
const DrawnPngSchema = z
  .string()
  .max(Math.ceil((signatureDrawnPngMaxBytes * 4) / 3) + 4)
  .regex(/^[A-Za-z0-9+/]+={0,2}$/)

export const PortalSignBodySchema = z
  .object({
    version: z.number().int().min(1),
    originalSha256: Sha256Schema,
    fullName: z.string().trim().min(1).max(120),
    method: z.enum(signatureMethods),
    drawnPng: DrawnPngSchema.optional(),
  })
  .superRefine((body, context) => {
    if (body.method === "drawn" && !body.drawnPng) {
      context.addIssue({
        code: "custom",
        path: ["drawnPng"],
        message: "Draw your signature.",
      })
    }
    if (body.method === "typed" && body.drawnPng) {
      context.addIssue({
        code: "custom",
        path: ["drawnPng"],
        message: "A typed signature has no drawing.",
      })
    }
  })
export type PortalSignBody = z.infer<typeof PortalSignBodySchema>

export const PortalSignResponseSchema = z.object({
  status: z.literal("signed"),
  signedFileId: z.uuid(),
  signedAt: z.iso.datetime(),
})
export type PortalSignResponse = z.infer<typeof PortalSignResponseSchema>

/** The firm's PDF to sign: PDF only, up to 10 MiB. */
export const ApiMintSignatureOriginalBodySchema = z.object({
  fileName: z.string().trim().min(1).max(240),
  byteSize: z.number().int().positive().max(signatureMaxBytes),
  contentType: z.literal("application/pdf"),
  contentHash: z.string().regex(/^(?:sha256:)?[0-9a-fA-F]{64}$/),
})
export type ApiMintSignatureOriginalBody = z.infer<
  typeof ApiMintSignatureOriginalBodySchema
>
